import { clientLogger } from '@/lib/clientLogger';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@/lib/api';
import { getStorageItem, removeStorageItem } from '@/lib/capacitor';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { AlertCircle } from 'lucide-react';
import { STRIPE_DEFAULT_RETURN, STRIPE_OAUTH_COMPANY_KEY, STRIPE_OAUTH_RETURN_KEY } from '@/lib/stripeConnect';

/**
 * Dedicated Stripe OAuth callback page.
 * Stripe redirects here with ?code=...&state=... after user authorizes.
 * This page exchanges the code via an authenticated API call,
 * then redirects back to the page that started it (/payment-provider, or the setup wizard's
 * Payments step) with ?connected=true or ?error=... appended.
 */
export function StripeCallbackPage() {
    const navigate = useNavigate();
    const handled = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [back, setBack] = useState(STRIPE_DEFAULT_RETURN);

    // Read params once from the URL
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const stripeError = params.get('error');
    const errorDescription = params.get('error_description');

    // If no code and no error, this page was accessed directly — block it
    const hasParams = !!(code || stripeError);

    useEffect(() => {
        if (handled.current) return;
        handled.current = true;

        if (!hasParams) return;

        const completeOAuth = async () => {
            // Saved by startStripeConnect before the redirect.
            const returnTo = await getStorageItem(STRIPE_OAUTH_RETURN_KEY) || STRIPE_DEFAULT_RETURN;
            await removeStorageItem(STRIPE_OAUTH_RETURN_KEY);
            setBack(returnTo);
            const finish = (result: string) =>
                navigate(`${returnTo}${returnTo.includes('?') ? '&' : '?'}${result}`, { replace: true });

            // Handle Stripe-side errors (user denied access)
            if (stripeError) {
                finish(`error=${encodeURIComponent(errorDescription || stripeError)}`);
                return;
            }

            if (!code || !state) {
                finish('error=missing_params');
                return;
            }

            try {
                // State is HMAC-signed and opaque — read companyId from storage
                // (saved by startStripeConnect before redirect)
                const stateCompanyId = await getStorageItem(STRIPE_OAUTH_COMPANY_KEY);
                const storedOrgId = await getStorageItem('selected_org_id');
                await removeStorageItem(STRIPE_OAUTH_COMPANY_KEY);

                if (storedOrgId) {
                    apiService.setOrgId(storedOrgId);
                }

                if (!stateCompanyId) {
                    finish('error=missing_company_context');
                    return;
                }

                await apiService.completeStripeOAuth(code, state, stateCompanyId);
                finish('connected=true');
            } catch (err: any) {
                clientLogger.error('Stripe OAuth complete error:', err);
                const message = err.response?.data?.details || err.message || 'Failed to complete Stripe connection';
                setError(message);
            }
        };

        completeOAuth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // No valid params — show "not accessible" message
    if (!hasParams) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-secondary-50 text-center p-8">
                <AlertCircle className="h-12 w-12 text-secondary-400 mb-4" />
                <h2 className="text-lg font-semibold text-secondary-900 mb-2">Nothing to do here</h2>
                <p className="text-secondary-500 mb-6">This page handles Stripe authorization callbacks and isn't accessible directly.</p>
                <button
                    onClick={() => navigate('/payment-provider', { replace: true })}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                    Go to Payment Provider
                </button>
            </div>
        );
    }

    // Error state — show error with link back
    if (error) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-secondary-50 text-center p-8">
                <AlertCircle className="h-12 w-12 text-error-500 mb-4" />
                <h2 className="text-lg font-semibold text-secondary-900 mb-2">Connection Failed</h2>
                <p className="text-secondary-500 mb-6">{error}</p>
                <button
                    onClick={() => navigate(back, { replace: true })}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                    {back === STRIPE_DEFAULT_RETURN ? 'Back to Payment Provider' : 'Back to setup'}
                </button>
            </div>
        );
    }

    // Processing state
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-secondary-50 text-center">
            <LoadingSpinner />
            <p className="mt-4 text-secondary-600 font-medium">Completing Stripe connection...</p>
        </div>
    );
}
